import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, ShoppingBasket, RefreshCw, Trash2, Check, ImageIcon, Heart, Utensils, ShoppingCart, ChevronDown } from "lucide-react";
import RecipeCardDialog from "@/components/RecipeCardDialog";
import MealReviewDialog from "@/components/MealReviewDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const ALL_MEALS = ["breakfast", "lunch", "dinner", "snack"];
const DAY_OPTIONS = [1, 2, 3];
const GROCERY_CATEGORIES = [
  "Grains & Flours",
  "Dairy and Poultry",
  "Meats",
  "Vegetables",
  "Pulses & Legumes",
  "Spices & Seasonings",
  "Oils & Cooking Fats",
  "Sauces, Pastes & Condiments",
  "Nuts, Seeds & Dry Fruits",
  "Canned & Shelf-Stable Foods",
  "Beverages & Specialty Ingredients",
];

export default function Planner() {
  const { user } = useAuth();
  const isPro = ["pro", "clinic"].includes(user?.profile?.tier);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [prefs, setPrefs] = useState("");
  const [usePantry] = useState(false);
  const [days, setDays] = useState(1);
  const [meals, setMeals] = useState(["breakfast"]);
  const [favorites, setFavorites] = useState([]);
  const [groceryOpen, setGroceryOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [cooking, setCooking] = useState(false);
  const [disliking, setDisliking] = useState(false);
  const [reviewTrigger, setReviewTrigger] = useState(null);

  const load = async () => {
    const { data } = await api.get("/planner/current");
    setPlan(data || null);
  };
  const loadFavorites = async () => {
    try { const { data } = await api.get("/recipes/favorites"); setFavorites(data || []); } catch (e) { console.error("Failed to load favorite recipes:", e); }
  };
  useEffect(() => { load(); loadFavorites(); }, []);

  const favKey = (dish, meal) => `${(dish || "").trim().toLowerCase()}|${meal || ""}`;
  const favForRecipe = recipe ? favorites.find((f) => favKey(f.dish, f.meal) === favKey(recipe.dish, recipe.meal)) : null;

  const openRecipe = async (date, meal, dish) => {
    const key = `${date}|${meal}`;
    setSelectedKey(key);
    setCardOpen(true);
    setRecipe(null);
    setRecipeLoading(true);
    try {
      const { data } = await api.post("/planner/recipe", { date, meal, dish_name: dish });
      setRecipe(data);
    } catch (e) {
      toast.error("Couldn't generate the recipe — please retry");
      setCardOpen(false);
    } finally {
      setRecipeLoading(false);
    }
  };

  const toggleLike = async () => {
    if (!recipe) return;
    const existing = favForRecipe;
    try {
      if (existing) {
        await api.delete(`/recipes/favorites/${existing.id}`);
        setFavorites((f) => f.filter((x) => x.id !== existing.id));
        toast.success("Removed from liked recipes");
      } else {
        const { data } = await api.post("/recipes/favorites", {
          dish: recipe.dish, meal: recipe.meal, date: recipe.date, recipe,
        });
        setFavorites((f) => [data, ...f]);
        toast.success("Saved to liked recipes");
      }
    } catch (e) {
      toast.error("Couldn't update liked recipes");
    }
  };

  const dislikeRecipe = async (r) => {
    const cur = r || recipe;
    if (!cur || disliking) return;
    setDisliking(true);
    try {
      await api.post("/recipes/dislike", { dish: cur.dish, meal: cur.meal });
      setFavorites((f) => f.filter((x) => favKey(x.dish, x.meal) !== favKey(cur.dish, cur.meal)));
      // Regenerate a different dish for the same slot.
      if (cur.date && selectedKey) {
        const { data } = await api.post("/planner/recipe", { date: cur.date, meal: cur.meal, dish_name: cur.dish, regenerate: true, avoid: [cur.dish] });
        setRecipe(data);
        load();
        toast.success(`Not a fan — here's ${data.dish} instead`);
      } else {
        // From favorites list (no plan slot): just close.
        setCardOpen(false);
        toast.success("Removed from your recipes");
      }
    } catch (e) {
      toast.error("Couldn't fetch another recipe — please retry");
    } finally {
      setDisliking(false);
    }
  };

  const openFavorite = (fav) => {
    setSelectedKey(null);
    setRecipe(fav.recipe && Object.keys(fav.recipe).length ? fav.recipe : { dish: fav.dish, meal: fav.meal, for_name: "", ingredients: [], steps: [], totals: {} });
    setRecipeLoading(false);
    setCardOpen(true);
  };

  const removeFavorite = async (e, fav) => {
    e.stopPropagation();
    await api.delete(`/recipes/favorites/${fav.id}`);
    setFavorites((f) => f.filter((x) => x.id !== fav.id));
  };

  const cookThis = async (r) => {
    if (!r) return;
    setCooking(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const t = r.totals || {};
      const { data: log } = await api.post("/food-logs", {
        name: r.dish,
        serving: r.servings ? `${r.servings} serving${r.servings > 1 ? "s" : ""}` : "1 serving",
        meal: ["breakfast", "lunch", "dinner", "snack"].includes(r.meal) ? r.meal : "snack",
        calories: Number(t.calories) || 0,
        protein_g: Number(t.protein_g) || 0,
        carbs_g: Number(t.carbs_g) || 0,
        fat_g: Number(t.fat_g) || 0,
        fiber_g: Number(t.fiber_g) || 0,
        logged_for: today,
      });
      toast.success(`Logged "${r.dish}" to your tracker`);
      setCardOpen(false);
      // Open pantry-deduction review seeded with the recipe's exact ingredients (no LLM call).
      setReviewTrigger({
        log_id: log.id,
        name: r.dish,
        ingredients: (r.ingredients || []).map((ing) => ({
          name: ing.name, quantity: ing.quantity, unit: ing.unit,
          calories: ing.calories, protein_g: ing.protein_g, carbs_g: ing.carbs_g,
          fat_g: ing.fat_g, fiber_g: ing.fiber_g,
        })),
      });
    } catch (e) {
      toast.error("Couldn't log this meal");
    } finally {
      setCooking(false);
    }
  };

  const toggleMeal = (m) =>
    setMeals((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : [...ALL_MEALS.filter((a) => cur.includes(a) || a === m)]));

  const generate = async () => {
    if (meals.length === 0) return toast.warning("Pick at least one meal");
    setLoading(true);
    try {
      const { data } = await api.post("/planner/generate", {
        preferences: prefs, use_pantry: usePantry, days, meals,
      });
      setPlan(data);
      toast.success(`${days}-day plan generated`);
    } catch (e) {
      toast.error("Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleGrocery = async (g) => {
    const newChecked = !g.checked;
    setPlan((p) => ({ ...p, grocery_list: (p.grocery_list || []).map((x) => (x.name === g.name ? { ...x, checked: newChecked } : x)) }));
    try {
      await api.patch("/planner/grocery", { name: g.name, checked: newChecked });
    } catch (e) {
      toast.error("Couldn't save — please retry");
      setPlan((p) => ({ ...p, grocery_list: (p.grocery_list || []).map((x) => (x.name === g.name ? { ...x, checked: !newChecked } : x)) }));
    }
  };

  const presentMeals = (d) => ALL_MEALS.filter((m) => d[m]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
          <div>
            <div className="text-overline">meal planner</div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-primary tracking-tight">Plan ahead, plate by plate</h1>
            <p className="text-foreground/70 mt-1">Pick a range from a single meal up to 3 days — generated from your pantry + preferences.</p>
          </div>
        </div>

        <div className="bg-surface-elevated border border-border rounded-3xl p-6 mb-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block">How many days?</Label>
              <div className="flex flex-wrap gap-2" data-testid="planner-days">
                {DAY_OPTIONS.map((n) => (
                  <button
                    key={n}
                    onClick={() => setDays(n)}
                    data-testid={`planner-day-${n}`}
                    className={`h-9 w-9 rounded-full text-sm font-medium transition border ${
                      days === n ? "bg-primary text-primary-foreground border-primary" : "border-border text-foreground/70 hover:bg-muted"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Which meals?</Label>
              <div className="flex flex-nowrap gap-1.5 overflow-x-auto scrollbar-none" data-testid="planner-meals">
                {ALL_MEALS.map((m) => {
                  const on = meals.includes(m);
                  return (
                    <button
                      key={m}
                      onClick={() => toggleMeal(m)}
                      data-testid={`planner-meal-${m}`}
                      className={`shrink-0 inline-flex items-center justify-center whitespace-nowrap px-2.5 h-8 rounded-full text-xs font-medium capitalize transition border ${
                        on ? "bg-secondary/15 text-secondary border-secondary/40" : "border-border text-foreground/60 hover:bg-muted"
                      }`}
                    >
                      {on && <Check size={12} className="mr-1" />}{m}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 items-end">
            <div className="md:col-span-2">
              <Label>Preferences</Label>
              <Textarea
                value={prefs}
                onChange={(e) => setPrefs(e.target.value)}
                rows={2}
                placeholder="e.g., vegetarian, high-protein, ~1800 kcal, Indian + Mediterranean"
                className="rounded-2xl bg-surface"
                data-testid="planner-prefs"
              />
            </div>
            <div className="flex flex-col gap-3">
              <Button onClick={generate} disabled={loading} className="rounded-full bg-primary hover:bg-primary/90 h-11" data-testid="generate-plan-btn">
                {loading ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
                {plan ? "Generate" : "Generate plan"}
              </Button>
            </div>
          </div>
        </div>

        {!plan && !loading && (
          <div className="bg-surface border border-dashed border-border rounded-3xl p-10 text-center">
            <ShoppingBasket className="mx-auto text-secondary mb-2" size={28} />
            <div className="font-serif-display text-2xl text-primary mb-1">No plan yet</div>
            <div className="text-foreground/70">Choose your days & meals, add preferences (optional), then tap Generate.</div>
          </div>
        )}

        {plan && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              <div className="text-overline mb-1">{plan.days?.length}-day menu</div>

              {!isPro && (
                <div className="flex items-center gap-3 rounded-2xl border border-secondary/30 bg-secondary/10 px-4 py-3" data-testid="pro-image-nudge">
                  <div className="shrink-0">
                    <ImageIcon size={20} className="text-secondary" />
                  </div>
                  <div className="text-sm flex-1">
                    <span className="font-medium text-primary">Plated dish photos</span>
                    <span className="text-foreground/70"> — see an AI image for every recipe.</span>
                  </div>
                  <span className="shrink-0 rounded-full bg-secondary/15 text-secondary text-xs font-medium px-3 py-1" data-testid="plated-coming-soon">
                    Coming soon
                  </span>
                </div>
              )}

              {plan.days?.map((d, i) => (
                <div key={i} className="bg-surface-elevated border border-border rounded-3xl p-5" data-testid={`plan-day-${i}`}>
                  <div className="flex items-baseline justify-between">
                    <div className="font-serif-display text-2xl text-primary">{new Date(d.date).toLocaleDateString(undefined, { weekday: "long" })}</div>
                    <div className="text-xs text-muted-foreground">{d.date}</div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    {presentMeals(d).map((m) => {
                      const key = `${d.date}|${m}`;
                      const selected = selectedKey === key;
                      const liked = favorites.some((f) => favKey(f.dish, f.meal) === favKey(d[m], m));
                      return (
                        <button
                          key={m}
                          onClick={() => openRecipe(d.date, m, d[m])}
                          data-testid={`meal-card-${i}-${m}`}
                          className={`text-left rounded-2xl p-3 border transition group ${
                            selected
                              ? "bg-green-50 border-green-300 ring-1 ring-green-300"
                              : "bg-surface border-border hover:border-secondary/40 hover:bg-secondary/5"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className={`text-overline truncate ${selected ? "text-green-700" : "text-secondary"}`}>{m}</div>
                            <span className="flex items-center gap-1 text-[10px] text-muted-foreground opacity-100 md:opacity-0 md:group-hover:opacity-100 transition shrink-0">
                              {liked && <Heart size={11} className="fill-green-600 text-green-600" />}
                              <Utensils size={11} /> recipe
                            </span>
                          </div>
                          <div className={`mt-0.5 text-sm leading-snug break-words ${selected ? "text-green-900" : "text-foreground/90"}`}>{d[m]}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-surface-elevated border border-border rounded-3xl p-5 h-fit lg:sticky lg:top-24" data-testid="grocery-section">
              {(() => {
                const items = plan.grocery_list || [];
                const total = items.length;
                const checkedCount = items.filter((g) => g.checked).length;
                const grouped = GROCERY_CATEGORIES
                  .map((cat) => [cat, items.filter((g) => (g.category || "Other") === cat)])
                  .filter(([, list]) => list.length);
                const other = items.filter((g) => !GROCERY_CATEGORIES.includes(g.category || "Other"));
                if (other.length) grouped.push(["Other", other]);
                return (
                  <>
                    <button
                      onClick={() => total > 0 && setGroceryOpen((o) => !o)}
                      className="w-full text-left flex items-center gap-3 active:scale-[0.99] transition"
                      data-testid="grocery-list-card"
                    >
                      <span className="grid place-items-center h-11 w-11 rounded-2xl bg-secondary/15 text-secondary shrink-0"><ShoppingCart size={20} /></span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-serif-display text-2xl text-primary leading-tight">Grocery List</span>
                        <span className="block text-sm text-foreground/70">
                          {total === 0 ? "Nothing extra to buy" : `${total} item${total > 1 ? "s" : ""} to buy${checkedCount ? ` · ${checkedCount} done` : ""}`}
                        </span>
                      </span>
                      {total > 0 && <ChevronDown size={20} className={`shrink-0 text-muted-foreground transition-transform duration-300 ${groceryOpen ? "rotate-180" : ""}`} />}
                    </button>

                    {groceryOpen && total > 0 && (
                      <div className="mt-4 max-h-[30rem] overflow-y-auto scrollbar-thin -mr-1 pr-1" data-testid="grocery-list">
                        <Accordion type="multiple" className="space-y-2">
                          {grouped.map(([cat, list]) => {
                            const done = list.filter((g) => g.checked).length;
                            return (
                              <AccordionItem key={cat} value={cat} className="border border-border rounded-2xl bg-surface px-3 overflow-hidden" data-testid={`grocery-category-${cat}`}>
                                <AccordionTrigger className="hover:no-underline py-3">
                                  <span className="flex items-center justify-between gap-2 flex-1 min-w-0">
                                    <span className="text-sm font-medium text-primary truncate">{cat}</span>
                                    <span className="text-xs text-muted-foreground shrink-0 tabular-nums">{done}/{list.length}</span>
                                  </span>
                                </AccordionTrigger>
                                <AccordionContent>
                                  <div className="space-y-0.5 pb-2">
                                    {list.map((g, i) => (
                                      <label key={i} className="flex items-center gap-2.5 py-1.5 cursor-pointer" data-testid={`grocery-item-${i}`}>
                                        <Checkbox checked={!!g.checked} onCheckedChange={() => toggleGrocery(g)} className="shrink-0" data-testid={`grocery-check-${g.name}`} />
                                        <span className={`flex-1 min-w-0 text-sm break-words leading-snug ${g.checked ? "line-through text-muted-foreground" : "text-foreground/90"}`}>{g.name}</span>
                                        <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">{g.quantity} {g.unit}</span>
                                      </label>
                                    ))}
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            );
                          })}
                        </Accordion>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Liked recipes history */}
        {favorites.length > 0 && (
          <div className="mt-10" data-testid="liked-recipes-section">
            <div className="text-overline mb-1 flex items-center gap-1"><Heart size={12} className="text-green-600" /> liked recipes</div>
            <p className="text-sm text-foreground/70 mb-4">Your saved favorites — tap to reopen the card or download again.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {favorites.map((fav) => (
                <button
                  key={fav.id}
                  onClick={() => openFavorite(fav)}
                  data-testid={`liked-recipe-${fav.id}`}
                  className="text-left bg-surface-elevated border border-border rounded-2xl p-4 hover:border-green-300 hover:bg-green-50/40 transition relative group"
                >
                  <div className="text-overline text-secondary capitalize">{fav.meal || "recipe"}</div>
                  <div className="font-serif-display text-lg text-primary leading-tight mt-0.5 pr-6 break-words">{fav.dish}</div>
                  {fav.recipe?.totals?.calories ? (
                    <div className="text-xs text-muted-foreground mt-1">{Math.round(fav.recipe.totals.calories)} kcal · P{Math.round(fav.recipe.totals.protein_g || 0)}/C{Math.round(fav.recipe.totals.carbs_g || 0)}/F{Math.round(fav.recipe.totals.fat_g || 0)}</div>
                  ) : null}
                  <span
                    onClick={(e) => removeFavorite(e, fav)}
                    className="absolute top-3 right-3 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition"
                    data-testid={`unlike-recipe-${fav.id}`}
                    role="button"
                  >
                    <Trash2 size={14} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <RecipeCardDialog
          open={cardOpen}
          recipe={recipe}
          loading={recipeLoading}
          liked={!!favForRecipe}
          onToggleLike={toggleLike}
          onCook={cookThis}
          cooking={cooking}
          onDislike={dislikeRecipe}
          disliking={disliking}
          onClose={() => { setCardOpen(false); setSelectedKey(null); }}
        />
        {reviewTrigger && (
          <MealReviewDialog
            trigger={reviewTrigger}
            onClose={() => setReviewTrigger(null)}
            onApplied={() => setReviewTrigger(null)}
          />
        )}
      </div>
    </Layout>
  );
}
